import { LogLevel } from '@unistocks/types';

export abstract class Logger {
  static logLevelMapper: Record<LogLevel, number> = {
    verbose: 0,
    debug: 1,
    info: 2,
    warn: 3,
    error: 4,
    silent: 5
  };

  static logLevel: number = Logger.logLevelMapper['debug'];
  static isJestEnvironment: boolean;
  static isDevMode: boolean;

  static init(
    logLevel: LogLevel,
    isDevMode: boolean = false,
    isJestEnvironment: boolean = false
  ): void {
    this.setLogLevel(logLevel);

    this.isDevMode = isDevMode;
    this.isJestEnvironment = isJestEnvironment;
  }

  static setLogLevel(logLevel: LogLevel): void {
    this.logLevel = this.logLevelMapper[logLevel];
  }

  /**
   * Writes a log when using the specified log level. Defaults to normal console.log
   */
  static log(message: any, level: LogLevel | null = null, ...data: any[]): void {
    switch (level) {
      case 'verbose':
        this.verbose(message, ...data);
        break;

      case 'debug':
        this.debug(message, ...data);
        break;

      case 'info':
        this.info(message, ...data);
        break;

      case 'warn':
        this.warn(message, ...data);
        break;

      case 'error':
        // Data expected to be an array with the first element being the error
        this.error(data[0], message);
        break;

      default:
        console.log(message, ...(data ?? ''));
    }
  }

  /**
   * Logs console messages when log level is set to debug or app is running in dev mode
   */
  static debug(message: any, ...data: any[]): void {
    if (
      !this.isJestEnvironment &&
      (this.logLevel <= this.logLevelMapper['debug'] || this.isDevMode)
    ) {
      if (data?.length) {
        console.log(message, ...data);
      } else {
        console.log(message);
      }
    }
  }

  /**
   * Logs console messages when log level is info
   */
  static info(message: any, ...data: any[]): void {
    if (this.logLevel <= this.logLevelMapper['info']) {
      console.info(message, ...data);
    }
  }

  /**
   * Logs console warnings when log level is set to warn or app is running in dev mode
   */
  static warn(message: any, ...data: any[]): void {
    if (this.logLevel <= this.logLevelMapper['warn'] || this.isDevMode) {
      console.warn(message, ...data);
    }
  }

  /**
   * Logs console errors when log level is set to error
   */
  static error(error?: any, customMessage?: string): void {
    if (this.logLevel <= this.logLevelMapper['error'] || this.isDevMode) {
      if (customMessage) console.error(customMessage, error);
      else console.error(error);
    }
  }

  /**
   * Logs only when log level is set to verbose
   */
  static verbose(message: any, ...data: any[]): void {
    if (this.logLevel === Logger.logLevelMapper['verbose']) {
      console.debug(message, ...data);
    }
  }
}
